'use client';

import { FC } from 'react';
import { PrismicRichText, PrismicImage } from '@prismicio/react';
import { Content } from '@prismicio/client';
import DynamicMarquee from '@/components/DynamicMarquee';

type Props = {
  slice: Content.IntroMediaGridSlice;
};

const HorizontalCarrouselSolidBg: FC<Props> = ({ slice }) => {
  const primary =
    slice.primary as Content.IntroMediaGridSliceHorizontalCarrouselSolidBgPrimary;
  const { title, description, pill, media_items: mediaItems } = primary;

  const items = mediaItems ?? [];
  const half = Math.ceil(items.length / 2);
  const topRow = items.slice(0, half);
  const bottomRow = items.slice(half);

  return (
    <section
      className="relative mx-5 overflow-hidden rounded-3xl bg-[#041118] md:mx-[30px]"
      data-slice-type={slice.slice_type}
    >
      <div className="flex flex-col items-center gap-6 px-4 pb-8 pt-16 text-center md:pb-10 md:pt-20 lg:px-[150px] lg:pb-12 lg:pt-[80px]">
        {pill && (
          <div className="inline-flex items-center rounded-full border border-[#00F557] px-4 py-2">
            <span className="text-[14px] font-normal uppercase leading-snug tracking-wide text-white">
              {pill}
            </span>
          </div>
        )}

        {title && (
          <div className="w-full max-w-3xl">
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => (
                  <h1 className="font-inter text-4xl font-semibold capitalize leading-tight text-white md:text-[42px]">
                    {children}
                  </h1>
                ),
              }}
            />
          </div>
        )}

        {description && (
          <div className="w-full max-w-[1000px]">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[16px] font-light leading-relaxed text-white">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="flex flex-col gap-6 pb-[80px]">
          {topRow.length > 0 && (
            <DynamicMarquee speed={40} gradient={false} direction="left">
              {topRow.map((item, idx) => (
                <div
                  key={`top-${idx}`}
                  className="mx-3 h-[180px] w-[280px] shrink-0 overflow-hidden rounded-lg md:h-[232px] md:w-[380px]"
                >
                  <PrismicImage
                    field={item.media}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </DynamicMarquee>
          )}

          {bottomRow.length > 0 && (
            <DynamicMarquee speed={40} gradient={false} direction="right">
              {bottomRow.map((item, idx) => (
                <div
                  key={`bottom-${idx}`}
                  className="mx-3 h-[180px] w-[280px] shrink-0 overflow-hidden rounded-lg md:h-[232px] md:w-[380px]"
                >
                  <PrismicImage
                    field={item.media}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </DynamicMarquee>
          )}
        </div>
      )}
    </section>
  );
};

export default HorizontalCarrouselSolidBg;
